// =============================================================================
// SaaS-Admin › Reminders engine
// Live data from /saastracker/admin/reminders/*.
//
// There is one template per (channel, event) across three channels — Email,
// WhatsApp and InApp — so the cards here are grouped by EVENT with a button per
// channel, rather than one card per template. InApp is not outbound: it is the
// popup the main app polls from /me/notifications and shows bottom-right.
//
// Events with no template still fire — ReminderService falls back to a built-in
// string — so the page lists every event that can fire, not just the configured
// ones, and says plainly which are running on the fallback.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../services/adminDataService';
import { getTrialPolicy } from '../services/pricingConfigService';

const CHANNELS: api.NotificationChannel[] = ['Email', 'WhatsApp', 'InApp'];

const CHANNEL_LABEL: Record<api.NotificationChannel, string> = {
  Email: 'Email',
  WhatsApp: 'WhatsApp',
  InApp: 'In-app',
};

/** Only these two carry a subject line; WhatsApp is body-only. */
const HAS_SUBJECT: api.NotificationChannel[] = ['Email', 'InApp'];

/** Events the sweep can raise that aren't credit thresholds. */
const FIXED_EVENTS = ['credit.exhausted', 'trial.ending', 'trial.ended', 'welcome'];

/** Merge fields ReminderService substitutes when rendering a template. */
const MERGE_FIELDS = ['TenantName', 'Balance', 'Currency', 'Percent', 'TrialEndsAt'];

const DEFAULT_THRESHOLDS = [50, 75, 90, 95];
const HISTORY_PAGE_SIZE = 25;

// ---- event vocabulary (mirrors ReminderService) ------------------------------

const thresholdOf = (ev: string): number =>
  ev.startsWith('threshold.') ? Number(ev.slice('threshold.'.length)) : NaN;

const eventTitle = (ev: string): string => {
  const n = thresholdOf(ev);
  if (!isNaN(n)) return `${n}% of credit used`;
  switch (ev) {
    case 'credit.exhausted': return 'Credit exhausted';
    case 'trial.ending': return 'Trial ending soon';
    case 'trial.ended': return 'Trial ended';
    case 'welcome': return 'Welcome';
    default: return ev;
  }
};

const triggerLabel = (ev: string): string => {
  const n = thresholdOf(ev);
  if (!isNaN(n)) return `On ${n}% credit usage`;
  switch (ev) {
    case 'credit.exhausted': return 'On full credit exhaustion';
    case 'trial.ending': return 'Shortly before the trial ends';
    case 'trial.ended': return 'When the trial ends';
    case 'welcome': return 'On tenant sign-up';
    default: return ev;
  }
};

/** Mirrors ReminderService.DefaultTitle — what the popup shows with no title set. */
const defaultTitle = (ev: string): string => {
  const n = thresholdOf(ev);
  if (!isNaN(n)) return `${n}% of credit used`;
  switch (ev) {
    case 'credit.exhausted': return 'Credit exhausted';
    case 'trial.ended': return 'Trial ended';
    case 'trial.ending': return 'Trial ending soon';
    default: return 'Notification';
  }
};

const isAutomated = (ev: string): boolean =>
  ev.startsWith('threshold.') || ev === 'credit.exhausted' || ev === 'trial.ended' || ev === 'trial.ending';

/** Thresholds ascending, then the lifecycle events, then anything unrecognised. */
const eventRank = (ev: string): number => {
  const n = thresholdOf(ev);
  if (!isNaN(n)) return 1000 + n;
  const i = FIXED_EVENTS.indexOf(ev);
  return i >= 0 ? 2000 + i : 3000;
};

// ---- formatting -------------------------------------------------------------

const relTime = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 'never';
  const secs = Math.round((Date.now() - t) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const fmtCredits = (n: number | null | undefined): string =>
  (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const channelIcon = (ch: api.NotificationChannel): string =>
  ch === 'Email' ? 'mail' : ch === 'WhatsApp' ? 'chat' : 'notifications';

const preview = (s: string, n: number): string =>
  s.length <= n ? s : `${s.slice(0, n).trimEnd()}…`;

/**
 * Mirrors ReminderService.SeverityFor — it decides the popup's left-border
 * colour and is fixed per event, not per template, so it is shown rather than
 * edited.
 */
const severityFor = (ev: string): 'info' | 'warning' | 'critical' => {
  if (ev === 'credit.exhausted' || ev === 'trial.ended') return 'critical';
  if (ev === 'threshold.90' || ev === 'threshold.95' || ev === 'trial.ending') return 'warning';
  return 'info';
};

/**
 * What a channel row shows and whether its toggle does anything.
 *
 * The asymmetry is real, not cosmetic. For Email/WhatsApp, `Active` only decides
 * WHICH COPY is used — an inactive (or missing) template still sends, on the
 * engine's built-in text. Only In-app's inactive record actually suppresses
 * delivery. Labelling both "Active" without saying so would be a lie the
 * operator only discovers from a tenant's inbox.
 */
interface ChannelState {
  on: boolean;
  label: string;
  state: string;
  canToggle: boolean;
  toggleTitle: string;
}

const channelState = (
  ch: api.NotificationChannel,
  t?: api.AdminReminderTemplateRow,
): ChannelState => {
  const inApp = ch === 'InApp';

  if (!t) {
    return {
      on: true,
      label: 'Active',
      state: inApp
        ? 'no template — popup reuses the email copy'
        : 'no template — built-in text is sent',
      // Switching In-app off is meaningful without copy; switching the outbound
      // channels off is not, because they send regardless.
      canToggle: inApp,
      toggleTitle: inApp
        ? 'Turn the popup off for this event'
        : 'Nothing to deactivate — with no template the engine sends built-in text',
    };
  }

  if (!t.active) {
    return {
      on: false,
      label: inApp ? 'Off' : 'Inactive',
      state: inApp ? 'no popup for this event' : 'inactive — built-in text is sent instead',
      canToggle: true,
      toggleTitle: inApp ? 'Show the popup again' : 'Use this copy again',
    };
  }

  return {
    on: true,
    label: 'Active',
    state: preview(t.subject || t.body, 42),
    canToggle: true,
    toggleTitle: inApp
      ? 'Turn the popup off for this event'
      : 'Stop using this copy — the engine falls back to built-in text',
  };
};

// ---- page -------------------------------------------------------------------

interface EventGroup {
  event: string;
  byChannel: Partial<Record<api.NotificationChannel, api.AdminReminderTemplateRow>>;
  totalSent: number;
  lastSentAt: string | null;
}

interface EditorState {
  id: string | null;
  event: string;
  channel: api.NotificationChannel;
  subject: string;
  body: string;
  active: boolean;
  isNew: boolean;
}

export const RemindersEngine: React.FC = () => {
  const [tab, setTab] = useState<'templates' | 'history'>('templates');

  const [templates, setTemplates] = useState<api.AdminReminderTemplateRow[]>([]);
  const [thresholds, setThresholds] = useState<number[]>(DEFAULT_THRESHOLDS);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [sweeping, setSweeping] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [audienceEvent, setAudienceEvent] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  // Escape or a click anywhere else closes the help popover.
  useEffect(() => {
    if (!showHelp) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowHelp(false); };
    const onDown = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) setShowHelp(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [showHelp]);

  // Stable identities: SendHistory and AudienceModal take these as effect deps,
  // and a fresh closure each render would re-fetch forever.
  const flash = useCallback((m: string) => {
    setErr(''); setMsg(m); window.setTimeout(() => setMsg(''), 3500);
  }, []);
  const fail = useCallback((e: any) => {
    setMsg(''); setErr(e?.message ?? String(e));
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      setTemplates(await api.getReminderTemplates());
    } catch (e) {
      fail(e);
    } finally {
      setLoading(false);
    }
  }, [fail]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Which threshold events exist is a config value, not a constant — read it so
  // the page lists exactly the ones the sweep will actually raise.
  useEffect(() => {
    getTrialPolicy()
      .then(p => {
        const parsed = (p?.thresholdsCsv ?? '')
          .split(',').map(s => Number(s.trim())).filter(n => Number.isFinite(n) && n > 0);
        if (parsed.length) setThresholds(parsed.sort((a, b) => a - b));
      })
      .catch(() => { /* fall back to the seeded defaults; not worth blocking the page */ });
  }, []);

  const groups = useMemo<EventGroup[]>(() => {
    const known = new Set<string>([
      ...thresholds.map(n => `threshold.${n}`),
      ...FIXED_EVENTS,
      ...templates.map(t => t.event),
    ]);

    return Array.from(known)
      .sort((a, b) => eventRank(a) - eventRank(b) || a.localeCompare(b))
      .map(event => {
        const rows = templates.filter(t => t.event === event);
        const byChannel: EventGroup['byChannel'] = {};
        rows.forEach(r => { byChannel[r.channel] = r; });
        return {
          event,
          byChannel,
          // Counted per EVENT server-side (one in-app record per firing), so it is
          // "times this reminder fired", not a per-channel delivery count.
          totalSent: rows.reduce((m, r) => Math.max(m, r.totalSent), 0),
          lastSentAt: rows.map(r => r.lastSentAt).filter(Boolean).sort().pop() ?? null,
        };
      });
  }, [templates, thresholds]);

  const configured = templates.length;

  const openEditor =(event: string, channel: api.NotificationChannel, t?: api.AdminReminderTemplateRow) => {
    setEditor({
      id: t?.id ?? null,
      event,
      channel,
      subject: t?.subject ?? '',
      body: t?.body ?? '',
      active: t?.active ?? true,
      isNew: !t,
    });
  };

  const saveEditor = async (state: EditorState) => {
    try {
      await api.saveReminderTemplate({
        id: state.id,
        channel: state.channel,
        event: state.event,
        subject: HAS_SUBJECT.includes(state.channel) ? state.subject : null,
        body: state.body,
        active: state.active,
      });
      setEditor(null);
      flash(`${eventTitle(state.event)} · ${CHANNEL_LABEL[state.channel]} saved`);
      await loadTemplates();
    } catch (e) {
      fail(e);
    }
  };

  const runSweep = async () => {
    if (!window.confirm(
      'Evaluate every tenant now and send whatever is due?\n\n' +
      'This sends real email/WhatsApp and can change tenant status.',
    )) return;

    setSweeping(true);
    try {
      const r = await api.runReminderSweep();
      flash(`Swept ${r.tenants} tenant${r.tenants === 1 ? '' : 's'} — ${r.sent} reminder${r.sent === 1 ? '' : 's'} sent.`);
      await loadTemplates();
    } catch (e) {
      fail(e);
    } finally {
      setSweeping(false);
    }
  };

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Reminders engine</h1>
          <p className="ov-header__sub">
            Automated messages triggered by credit consumption and trial state.
          </p>
        </div>
        <div className="ov-header__actions">
          <button className="btn-small" onClick={loadTemplates} disabled={loading}>
            <span className="material-icons">refresh</span> Refresh
          </button>
          <button className="btn-small btn-small--primary" onClick={runSweep} disabled={sweeping}>
            <span className="material-icons">bolt</span> {sweeping ? 'Running…' : 'Run sweep now'}
          </button>
        </div>
      </header>

      {(msg || err) && (
        <div style={{
          margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: err ? '#fdecea' : '#e7f6ec', color: err ? '#c0392b' : '#1e7e46',
          border: `1px solid ${err ? '#f5c6cb' : '#bfe6cd'}`,
        }}>
          {err || msg}
          {err && <button className="btn-small" style={{ marginLeft: 10 }} onClick={loadTemplates}>Retry</button>}
        </div>
      )}

      <div className="rem-tabbar">
        {/* The help button sits outside the tablist so the list contains only
            tabs — assistive tech would otherwise announce it as one. */}
        <nav className="rem-tabs rem-tabs--flush" role="tablist" aria-label="Reminders">
          <button
            role="tab" id="rem-tab-templates" aria-controls="rem-panel-templates"
            aria-selected={tab === 'templates'}
            className={`rem-tab${tab === 'templates' ? ' rem-tab--active' : ''}`}
            onClick={() => setTab('templates')}
          >
            Templates ({configured})
          </button>
          <button
            role="tab" id="rem-tab-history" aria-controls="rem-panel-history"
            aria-selected={tab === 'history'}
            className={`rem-tab${tab === 'history' ? ' rem-tab--active' : ''}`}
            onClick={() => setTab('history')}
          >
            Send history
          </button>
        </nav>

        <div className="rem-help" ref={helpRef}>
          <button
            type="button"
            className="rem-help__btn"
            aria-expanded={showHelp}
            aria-label="How this page works"
            title="How this page works"
            onClick={() => setShowHelp(v => !v)}
          >
            <span className="material-icons">help_outline</span>
          </button>

          {showHelp && (
            <div className="rem-help__panel" role="dialog" aria-label="How this page works">
              <button
                type="button"
                className="rem-help__close"
                aria-label="Close"
                onClick={() => setShowHelp(false)}
              >
                <span className="material-icons">close</span>
              </button>

              {tab === 'templates' ? (
                <>
                  <p>
                    Every event below fires on its own. Where no template exists the engine
                    still sends, using a built-in fallback — so "no template" means unbranded,
                    not silent. Thresholds come from Pricing &amp; Credits and re-arm after a
                    top-up.
                  </p>
                  <p>
                    <strong>In-app</strong> is the popup the main application shows
                    bottom-right; it persists until the user dismisses it. Without its own
                    template it reuses the email copy, which is usually too long for a 360px
                    toast. Its colour is set by the event, not the template.
                  </p>
                </>
              ) : (
                <p>
                  Every reminder actually delivered, newest first. "Opened" means the tenant
                  saw the in-app popup — email and WhatsApp opens are not tracked.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {tab === 'templates' && (
        <div role="tabpanel" id="rem-panel-templates" aria-labelledby="rem-tab-templates">
          {loading && templates.length === 0 ? (
            <div className="empty">
              <span className="material-icons">hourglass_empty</span>
              Loading templates…
            </div>
          ) : (
            <div className="templates-grid">
              {groups.map(g => (
                <div className="template-card" key={g.event}>
                  <div className="template-card__head">
                    <div>
                      <div className="template-card__name">{eventTitle(g.event)}</div>
                      <div className="template-card__trigger">
                        <span className="material-icons">bolt</span>
                        {triggerLabel(g.event)}
                      </div>
                    </div>
                    <span className={`status-pill status-pill--${isAutomated(g.event) ? 'active' : 'canceled'}`}>
                      {isAutomated(g.event) ? 'auto' : 'manual'}
                    </span>
                  </div>

                  <div className="template-card__stats">
                    <div>
                      <div className="template-card__stat-num">{g.totalSent.toLocaleString('en-US')}</div>
                      <div className="template-card__stat-label">times fired</div>
                    </div>
                    <div>
                      <div className="template-card__stat-num template-card__stat-num--small">
                        {relTime(g.lastSentAt)}
                      </div>
                      <div className="template-card__stat-label">last fired</div>
                    </div>
                    <div>
                      <div className="template-card__stat-num template-card__stat-num--small">
                        {Object.keys(g.byChannel).length} / {CHANNELS.length}
                      </div>
                      <div className="template-card__stat-label">channels set</div>
                    </div>
                  </div>

                  {/* One row, one button per channel. Everything else about a
                      channel — its copy and its Active switch — lives in the
                      popup the button opens. */}
                  <div className="rem-chan-row">
                    {CHANNELS.map(ch => {
                      const t = g.byChannel[ch];
                      const cs = channelState(ch, t);
                      return (
                        <button
                          type="button"
                          key={ch}
                          className={
                            'rem-chan-btn'
                            + (cs.on ? ' rem-chan-btn--on' : '')
                            + (t ? '' : ' rem-chan-btn--empty')
                          }
                          onClick={() => openEditor(g.event, ch, t)}
                          title={`${CHANNEL_LABEL[ch]} — ${cs.state}. Click to ${t ? 'edit' : 'create'}.`}
                        >
                          <span className="material-icons">{channelIcon(ch)}</span>
                          <span className="rem-chan-btn__label">{CHANNEL_LABEL[ch]}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="rem-card__foot">
                    <button
                      className="btn-small btn-small--primary"
                      onClick={() => setAudienceEvent(g.event)}
                    >
                      <span className="material-icons">send</span> Run
                    </button>
                    <span className="rem-card__foot-note">
                      see who this applies to and send now
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div role="tabpanel" id="rem-panel-history" aria-labelledby="rem-tab-history">
          <SendHistory events={groups.map(g => g.event)} onError={fail} />
        </div>
      )}

      {editor && (
        <TemplateEditor
          state={editor}
          onChange={setEditor}
          onCancel={() => setEditor(null)}
          onSave={saveEditor}
        />
      )}

      {audienceEvent && (
        <AudienceModal
          event={audienceEvent}
          onClose={() => setAudienceEvent(null)}
          onSent={m => { flash(m); loadTemplates(); }}
          onError={fail}
        />
      )}
    </div>
  );
};

// ---- per-event audience + targeted send -------------------------------------

/**
 * Lists the tenants this one event currently applies to and sends it to the
 * ticked ones. Nothing is selected by default: this button sends real messages,
 * so arming it has to be a deliberate act rather than the resting state.
 */
const AudienceModal: React.FC<{
  event: string;
  onClose: () => void;
  onSent: (message: string) => void;
  onError: (e: any) => void;
}> = ({ event, onClose, onSent, onError }) => {
  const [data, setData] = useState<api.AdminReminderAudienceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getReminderAudience(event)
      .then(r => { if (alive) setData(r); })
      .catch(e => { if (alive) onError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [event, onError]);

  const items = data?.items ?? [];
  const allSelected = items.length > 0 && selected.size === items.length;

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(items.map(i => i.tenant.id)));

  /** Ticked tenants who have had this reminder before — they will get it twice. */
  const resendCount = items.filter(i => selected.has(i.tenant.id) && i.alreadySent).length;

  // No confirm dialog: selecting tenants and pressing a button that names the
  // count is the deliberate act. The re-send warning below states the risk in
  // place, rather than in a modal on top of a modal.
  const send = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;

    setSending(true);
    try {
      const r = await api.sendReminder(event, ids);
      onSent(`Sent “${eventTitle(event)}” to ${r.sent} tenant${r.sent === 1 ? '' : 's'}.`);
      onClose();
    } catch (e) {
      onError(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rem-modal" onClick={onClose}>
      <div
        className="rem-modal__box rem-modal__box--wide"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="rem-modal__head">
          <div>
            <div className="rem-modal__title">Run · {eventTitle(event)}</div>
            <div className="rem-modal__sub">
              {data ? data.match : triggerLabel(event)} · <code>{event}</code>
            </div>
          </div>
          <button className="btn-small btn-small--ghost" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </header>

        <div className="rem-modal__body">
          {loading && <div className="viz-empty">Finding matching tenants…</div>}

          {!loading && items.length === 0 && (
            <div className="viz-empty">
              No tenant matches this event right now.
            </div>
          )}

          {!loading && items.length > 0 && (
            <>
              <label className="rem-check" style={{ marginBottom: 10 }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                Select all {items.length} tenant{items.length === 1 ? '' : 's'}
              </label>

              <div className="rem-aud">
                <div className="rem-aud__head">
                  <div />
                  <div>Tenant</div>
                  <div>Plan</div>
                  <div>Credit used</div>
                  <div>Balance</div>
                  <div>This reminder</div>
                </div>

                {items.map(row => {
                  const t = row.tenant;
                  const on = selected.has(t.id);
                  return (
                    <label className={`rem-aud__row${on ? ' rem-aud__row--on' : ''}`} key={t.id}>
                      <div><input type="checkbox" checked={on} onChange={() => toggle(t.id)} /></div>
                      <div className="rem-aud__tenant">
                        <span className="rem-aud__name">{t.name}</span>
                        <span className="rem-aud__mail">{t.email || 'no email on file'}</span>
                      </div>
                      <div>
                        <span className={`plan-tag plan-tag--${t.plan ? t.plan.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'none'}`}>
                          {t.plan ? t.plan.toUpperCase() : 'NONE'}
                        </span>
                      </div>
                      <div className="rem-aud__num">{t.pctUsed}%</div>
                      <div className="rem-aud__num">{fmtCredits(t.balance)}</div>
                      <div className="rem-aud__sent">
                        {row.alreadySent
                          ? <><span className="hist-status hist-status--delivered">sent</span>{' '}
                              <em>{relTime(row.lastSentAt)}</em></>
                          : <span className="hist-status hist-status--sent">not sent</span>}
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <footer className="rem-modal__foot">
          <span className="rem-aud__count">
            {selected.size} of {items.length} selected
            {resendCount > 0 && (
              <span className="rem-aud__warn">
                <span className="material-icons">warning</span>
                {resendCount} already received this — sending gives them a second copy
              </span>
            )}
          </span>
          <button className="btn-small btn-small--ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn-small btn-small--primary"
            onClick={send}
            disabled={sending || selected.size === 0}
          >
            {sending ? 'Sending…' : `Send now to ${selected.size}`}
          </button>
        </footer>
      </div>
    </div>
  );
};

// ---- send history -----------------------------------------------------------

const SendHistory: React.FC<{ events: string[]; onError: (e: any) => void }> = ({ events, onError }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [event, setEvent] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<api.AdminReminderHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = window.setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const res = await api.getReminderHistory({ search, event, page, pageSize: HISTORY_PAGE_SIZE });
      if (id === reqId.current) setData(res);
    } catch (e) {
      if (id === reqId.current) { setData(null); onError(e); }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [search, event, page, onError]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  return (
    <>
      <div className="filters">
        <div className="filters__search">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder="Search by tenant or title…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
        <div className="filters__group">
          <label>Event</label>
          <select value={event} onChange={e => { setEvent(e.target.value); setPage(1); }}>
            <option value="all">All events</option>
            {events.map(ev => <option key={ev} value={ev}>{eventTitle(ev)}</option>)}
          </select>
        </div>
        <button
          className="btn-small btn-small--ghost"
          onClick={() => { setSearchInput(''); setSearch(''); setEvent('all'); setPage(1); }}
        >
          Reset
        </button>
      </div>

      <div className="hist-table">
        <div className="hist-table__head">
          <div>When</div><div>Tenant</div><div>Event</div><div>Message</div><div>Status</div>
        </div>

        {items.map(h => (
          <div className="hist-row" key={h.id}>
            <div className="hist-row__when">{fmtDateTime(h.sentAt)}</div>
            <div>{h.tenantName}</div>
            <div>
              <span className={`sev-dot sev-dot--${h.severity}`} title={`${h.severity} severity`} />
              {eventTitle(h.event)}
            </div>
            <div title={h.body}>{preview(h.title, 48)}</div>
            <div><span className={`hist-status hist-status--${h.status}`}>{h.status}</span></div>
          </div>
        ))}

        {loading && items.length === 0 && (
          <div className="empty">
            <span className="material-icons">hourglass_empty</span>
            Loading history…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="empty">
            <span className="material-icons">inbox</span>
            No reminders have been sent yet.
          </div>
        )}
      </div>

      {total > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 12, fontSize: 12, color: '#6b7684',
        }}>
          <span>
            Showing {(page - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(page * HISTORY_PAGE_SIZE, total)} of{' '}
            {total.toLocaleString('en-US')}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-small" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              <span className="material-icons">chevron_left</span> Prev
            </button>
            <span>Page {page} of {pageCount}</span>
            <button className="btn-small" disabled={page >= pageCount || loading} onClick={() => setPage(p => p + 1)}>
              Next <span className="material-icons">chevron_right</span>
            </button>
          </span>
        </div>
      )}
    </>
  );
};

// ---- editor -----------------------------------------------------------------

const TemplateEditor: React.FC<{
  state: EditorState;
  onChange: (s: EditorState) => void;
  onCancel: () => void;
  onSave: (s: EditorState) => void | Promise<void>;
}> = ({ state, onChange, onCancel, onSave }) => {
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  /** Drops a merge field where the caret is, rather than always at the end. */
  const insertField = (field: string) => {
    const token = `{{${field}}}`;
    const el = bodyRef.current;
    if (!el) { onChange({ ...state, body: state.body + token }); return; }
    const start = el.selectionStart ?? state.body.length;
    const end = el.selectionEnd ?? start;
    const next = state.body.slice(0, start) + token + state.body.slice(end);
    onChange({ ...state, body: next });
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const submit = async () => {
    setSaving(true);
    try { await onSave(state); } finally { setSaving(false); }
  };

  return (
    <div className="rem-modal" onClick={onCancel}>
      <div className="rem-modal__box" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="rem-modal__head">
          <div>
            <div className="rem-modal__title">
              {state.isNew ? 'Create' : 'Edit'} {CHANNEL_LABEL[state.channel]} template
            </div>
            <div className="rem-modal__sub">
              {eventTitle(state.event)} · {triggerLabel(state.event)} ·{' '}
              <code>{state.event}</code>
            </div>
          </div>
          <button className="btn-small btn-small--ghost" onClick={onCancel}>
            <span className="material-icons">close</span>
          </button>
        </header>

        <div className="rem-modal__body">
          {HAS_SUBJECT.includes(state.channel) && (
            <div className="rem-field">
              <label htmlFor="rem-subject">
                {state.channel === 'InApp' ? 'Popup title' : 'Subject'}
              </label>
              <input
                id="rem-subject"
                className="rem-input"
                value={state.subject}
                placeholder={state.channel === 'InApp'
                  ? 'e.g. {{Percent}}% of credit used'
                  : 'e.g. You have used {{Percent}}% of your credit'}
                onChange={e => onChange({ ...state, subject: e.target.value })}
              />
              <p className="rem-hint">
                {state.channel === 'InApp'
                  ? 'Keep it short — the popup is 360px wide. Left blank, a built-in title is used.'
                  : 'Also used as the popup title, unless this event has its own In-app template.'}
              </p>
            </div>
          )}

          <div className="rem-field">
            <label htmlFor="rem-body">Message</label>
            <textarea
              id="rem-body"
              ref={bodyRef}
              className="rem-textarea"
              rows={8}
              value={state.body}
              placeholder="Hi {{TenantName}}, you have {{Balance}} {{Currency}} of credit left."
              onChange={e => onChange({ ...state, body: e.target.value })}
            />
            <div className="rem-merge">
              <span className="rem-merge__label">Insert:</span>
              {MERGE_FIELDS.map(f => (
                <button type="button" key={f} className="merge-chip" onClick={() => insertField(f)}>
                  {`{{${f}}}`}
                </button>
              ))}
            </div>
          </div>

          {state.channel === 'InApp' && (
            <div className="rem-field">
              <label>Preview</label>
              <div className="rem-preview">
                <div className={`rem-toast rem-toast--${severityFor(state.event)}`}>
                  <span className="rem-toast__close">×</span>
                  <div className="rem-toast__title">
                    {state.subject.trim() || defaultTitle(state.event)}
                  </div>
                  <div className="rem-toast__body">{state.body || '…'}</div>
                </div>
              </div>
              <p className="rem-hint">
                Actual size and colour. Merge fields are shown as written — they resolve
                when the reminder fires.
              </p>
            </div>
          )}

          {/* The Active switch lives here rather than on the card, so a channel's
              copy and its on/off state are changed — and saved — together. */}
          <div className="rem-field rem-field--toggle">
            <div className="rem-field__text">
              <label>Status</label>
              <p className="rem-hint">
                {state.channel === 'InApp'
                  ? (state.active
                      ? 'The popup is shown for this event.'
                      : 'No popup is shown for this event at all.')
                  : (state.active
                      ? 'This copy is used when the reminder fires.'
                      : `Built-in text is sent instead — this does not stop ${CHANNEL_LABEL[state.channel]} delivery.`)}
              </p>
            </div>
            <button
              type="button"
              className={`rem-toggle${state.active ? ' rem-toggle--on' : ''}`}
              onClick={() => onChange({ ...state, active: !state.active })}
              aria-pressed={state.active}
            >
              <span className="rem-toggle__dot" />
              {state.active ? 'Active' : state.channel === 'InApp' ? 'Off' : 'Inactive'}
            </button>
          </div>
        </div>

        <footer className="rem-modal__foot">
          <button className="btn-small btn-small--ghost" onClick={onCancel}>Cancel</button>
          <button
            className="btn-small btn-small--primary"
            onClick={submit}
            // Copy is required to switch a channel ON, but not to switch it off —
            // turning the In-app popup off shouldn't force you to write a message
            // that will never be shown.
            disabled={saving || (state.active && !state.body.trim())}
          >
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </footer>
      </div>
    </div>
  );
};
